import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, TrendingUp, TrendingDown, Minus, Trophy, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLeaderboard, getBadgeInfo, getRankDescriptor, type LeaderboardEntry } from '@/hooks/useLeaderboard';
import { useAuth } from '@/hooks/useAuth';
import { Progress } from '@/components/ui/progress';

const RANK_MEDALS = ['🥇', '🥈', '🥉'];
const RANK_GLOW = [
  'from-amber-500/25 via-yellow-400/15 to-amber-600/25 border-amber-400/40',
  'from-slate-300/25 via-gray-200/15 to-slate-400/25 border-slate-300/40',
  'from-orange-600/25 via-amber-500/15 to-orange-700/25 border-orange-500/40',
];

function RewardBanner() {
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="relative overflow-hidden rounded-2xl border border-amber-400/30 bg-gradient-to-r from-amber-500/10 via-purple-500/10 to-amber-500/10 p-4 mb-4"
    >
      <motion.div
        className="absolute inset-0 rounded-2xl border-2 border-amber-400/20 pointer-events-none"
        animate={{ opacity: [0.2, 0.5, 0.2] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />
      <div className="relative flex items-center gap-3">
        <Trophy className="h-6 w-6 text-amber-400 shrink-0" />
        <div>
          <p className="text-sm font-bold text-foreground">🏆 Monthly Top Voices Win Rewards</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Finish Top 3 for 3 months in a row and unlock a free upgrade.
          </p>
        </div>
      </div>
    </motion.div>
  );
}

function MotivationStrip({ userEntry, topEntries }: { userEntry: LeaderboardEntry | null; topEntries: LeaderboardEntry[] }) {
  if (!userEntry || !userEntry.current_rank) return null;

  const rank = userEntry.current_rank;
  const top3Score = topEntries[2]?.total_score || 0;
  const pointsToTop3 = Math.max(0, top3Score - userEntry.total_score + 1);
  const rankChange = userEntry.previous_rank && userEntry.current_rank
    ? userEntry.previous_rank - userEntry.current_rank
    : 0;

  let message = '';
  let icon = <Sparkles className="h-3.5 w-3.5" />;

  if (rank <= 3) {
    message = "You're in the Top 3 — amazing work! 🔥";
  } else if (rank <= 10) {
    if (rankChange > 0) {
      message = `You moved up ${rankChange} spot${rankChange > 1 ? 's' : ''} today ⬆`;
    } else if (rankChange < 0) {
      message = `You dropped ${Math.abs(rankChange)} spot${Math.abs(rankChange) > 1 ? 's' : ''} — stay active`;
    } else {
      message = `You're ${pointsToTop3} point${pointsToTop3 !== 1 ? 's' : ''} away from Top 3`;
    }
  } else {
    message = `You're ranked #${rank} — keep going ❤️`;
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.3 }}
      className="flex items-center gap-2 rounded-xl bg-primary/8 border border-primary/15 px-3 py-2 mb-3"
    >
      {icon}
      <span className="text-xs font-medium text-foreground">{message}</span>
    </motion.div>
  );
}

function RankChange({ current, previous }: { current: number | null; previous: number | null }) {
  if (!current || !previous || current === previous) {
    return <Minus className="h-3 w-3 text-muted-foreground/50" />;
  }
  if (current < previous) {
    return (
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className="flex items-center gap-0.5 text-emerald-500"
      >
        <TrendingUp className="h-3 w-3" />
        <span className="text-[10px] font-bold">+{previous - current}</span>
      </motion.div>
    );
  }
  return (
    <div className="flex items-center gap-0.5 text-rose-400">
      <TrendingDown className="h-3 w-3" />
      <span className="text-[10px] font-bold">{current - previous}</span>
    </div>
  );
}

function TopCard({ entry, index }: { entry: LeaderboardEntry; index: number }) {
  const badge = getBadgeInfo(entry.badge_key);
  const descriptor = getRankDescriptor(entry.current_rank);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: index * 0.12, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br ${RANK_GLOW[index]} backdrop-blur-sm p-4 md:p-5`}
    >
      {/* Animated glow pulse for all top 3 */}
      <motion.div
        className={`absolute inset-0 rounded-2xl border-2 pointer-events-none ${
          index === 0 ? 'border-amber-400/40' : index === 1 ? 'border-slate-300/30' : 'border-orange-500/30'
        }`}
        animate={{ opacity: [0.2, 0.6, 0.2] }}
        transition={{ duration: 2.5 + index * 0.5, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Shine sweep effect */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.08) 45%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.08) 55%, transparent 60%)',
        }}
        animate={{ x: ['-100%', '200%'] }}
        transition={{ duration: 4, repeat: Infinity, repeatDelay: 3, ease: 'easeInOut' }}
      />

      <div className="relative flex items-center gap-3">
        <div className="text-3xl shrink-0">{RANK_MEDALS[index]}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-bold text-foreground truncate">{entry.display_name}</span>
            <span className="text-sm">{badge.icon}</span>
            <RankChange current={entry.current_rank} previous={entry.previous_rank} />
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 italic">{descriptor}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-extrabold text-foreground tabular-nums">{entry.total_score}</div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">pts</p>
        </div>
      </div>
    </motion.div>
  );
}

function RankRow({ entry, index }: { entry: LeaderboardEntry; index: number }) {
  const badge = getBadgeInfo(entry.badge_key);

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.36 + index * 0.06 }}
      className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-muted/40 transition-colors"
    >
      <span className="w-6 text-center text-sm font-bold text-muted-foreground tabular-nums">
        {entry.current_rank}
      </span>
      <span className="text-sm">{badge.icon}</span>
      <span className="flex-1 text-sm font-medium text-foreground truncate">
        {entry.display_name}
      </span>
      <RankChange current={entry.current_rank} previous={entry.previous_rank} />
      <span className="font-bold text-sm tabular-nums text-foreground">{entry.total_score}</span>
    </motion.div>
  );
}

function RankMovementPill({ userEntry }: { userEntry: LeaderboardEntry | null }) {
  if (!userEntry?.current_rank || !userEntry?.previous_rank) return null;
  const diff = userEntry.previous_rank - userEntry.current_rank;
  if (diff === 0) return null;

  const isUp = diff > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold ${
        isUp
          ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'
          : 'bg-rose-500/15 text-rose-400 border border-rose-500/20'
      }`}
    >
      {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {isUp ? `Up ${diff} spot${diff > 1 ? 's' : ''}` : `Down ${Math.abs(diff)} spot${Math.abs(diff) > 1 ? 's' : ''}`}
    </motion.div>
  );
}

export function ImpactLeaderboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { topEntries, userEntry, isLoading } = useLeaderboard();

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-border/50 bg-card/80 backdrop-blur-sm p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-muted rounded w-48" />
          <div className="h-3 bg-muted rounded w-64" />
          <div className="space-y-3 mt-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-muted/60 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (topEntries.length === 0) return null;

  const top3 = topEntries.slice(0, 3);
  const rest = topEntries.slice(3, 7); // Show ranks 4-7
  const userInTop = topEntries.some(e => e.user_id === user?.id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="relative overflow-hidden rounded-2xl border border-border/50 bg-card/90 backdrop-blur-sm"
    >
      {/* Ambient background glow */}
      <div className="absolute top-0 right-0 w-48 h-48 bg-primary/5 rounded-full blur-[60px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-accent/5 rounded-full blur-[50px] pointer-events-none" />

      <div className="relative px-5 pt-5 pb-2">
        {/* Reward Banner */}
        <RewardBanner />

        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <motion.h2
            className="text-lg md:text-xl font-extrabold text-foreground tracking-tight"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            🔥 Impact Leaderboard
          </motion.h2>
          <RankMovementPill userEntry={userEntry} />
        </div>
        <motion.p
          className="text-sm text-muted-foreground"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          People who are saying what matters most
        </motion.p>

        {/* Motivation Strip */}
        <div className="mt-3">
          <MotivationStrip userEntry={userEntry} topEntries={topEntries} />
        </div>
      </div>

      {/* Top 3 Featured Cards */}
      <div className="px-5 space-y-3">
        {top3.map((entry, i) => (
          <TopCard key={entry.id} entry={entry} index={i} />
        ))}
      </div>

      {/* Rank List (4-7) */}
      {rest.length > 0 && (
        <div className="px-5 mt-3 pb-1">
          <div className="border-t border-border/30 pt-2">
            {rest.map((entry, i) => (
              <RankRow key={entry.id} entry={entry} index={i} />
            ))}
          </div>
        </div>
      )}

      {/* "Almost there" nudge for rank 4-10 */}
      {userEntry && userEntry.current_rank && userEntry.current_rank > 3 && userEntry.current_rank <= 10 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mx-5 mt-2 rounded-xl bg-amber-500/8 border border-amber-500/15 px-3 py-2 text-center"
        >
          <p className="text-xs font-medium text-amber-300/90">
            ✨ You're close to the top — keep going
          </p>
        </motion.div>
      )}

      {/* User Position (if not in visible list) */}
      {!userInTop && userEntry && userEntry.current_rank && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mx-5 mt-3 rounded-xl bg-primary/8 border border-primary/15 p-3 flex items-center gap-3"
        >
          <span className="text-2xl">✨</span>
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground">
              You're ranked #{userEntry.current_rank}
            </p>
            <p className="text-xs text-muted-foreground">Keep going — every message moves you up ❤️</p>
          </div>
          <span className="font-bold text-sm tabular-nums">{userEntry.total_score} pts</span>
        </motion.div>
      )}

      {/* CTA */}
      <div className="p-4 sm:p-5 pt-4">
        <div className="rounded-xl bg-gradient-to-r from-primary/8 to-accent/8 border border-primary/15 p-3 sm:p-4 text-center box-border">
          <p className="text-sm text-foreground mb-3">
            Someone in your life is waiting to hear from you.
          </p>
          <Button
            onClick={() => navigate('/create')}
            size="lg"
            className="w-full max-w-full rounded-xl gap-2 font-bold shadow-lg shadow-primary/20 px-4 text-sm sm:text-base whitespace-normal h-auto min-h-12 py-3 leading-tight"
          >
            <span className="flex-1 text-center">Send something that matters</span>
            <ArrowRight className="h-4 w-4 shrink-0" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
